/**
 * Offline queue — stores failed mutations and replays them when back online.
 * Uses localStorage for persistence across sessions.
 */

const QUEUE_KEY = "elevate_offline_queue";

// 7 days instead of 24 hours — gives users a full week to reconnect
const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

function getQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) {
      console.warn("Offline queue was corrupted (not an array), resetting");
      localStorage.removeItem(QUEUE_KEY);
      return [];
    }
    return parsed;
  } catch (e) {
    console.error("Offline queue corrupted, data may have been lost:", e);
    // Try to preserve the raw data in a backup key before clearing
    try {
      const raw = localStorage.getItem(QUEUE_KEY);
      if (raw) {
        localStorage.setItem(QUEUE_KEY + "_backup", raw);
      }
    } catch { /* storage might be full */ }
    localStorage.removeItem(QUEUE_KEY);
    return [];
  }
}

function saveQueue(queue) {
  try {
    localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
  } catch (e) {
    // localStorage is full — this is critical, user will lose data
    console.error("Failed to save offline queue — storage full:", e);
    // Try to keep at least the newest items by trimming old ones
    if (queue.length > 1) {
      const trimmed = queue.slice(Math.floor(queue.length / 2));
      try {
        localStorage.setItem(QUEUE_KEY, JSON.stringify(trimmed));
      } catch { /* truly out of space */ }
    }
  }
}

/** Add a failed operation to the offline queue */
export function enqueue(operation) {
  const queue = getQueue();
  queue.push({
    ...operation,
    id: crypto.randomUUID(),
    timestamp: Date.now(),
  });
  saveQueue(queue);
}

/** Get pending operations count */
export function getPendingCount() {
  return getQueue().length;
}

/**
 * Process the queue — call with a handler map { operationType: asyncFn }
 * Returns { processed, expired } so the caller can notify the user about lost data.
 */
export async function processQueue(handlers) {
  const queue = getQueue();
  if (queue.length === 0) return { processed: 0, expired: 0 };

  const failed = [];
  let processed = 0;
  let expired = 0;

  for (const op of queue) {
    const handler = handlers[op.type];
    if (!handler) {
      failed.push(op);
      continue;
    }

    // Check if this item has expired
    if (Date.now() - op.timestamp > MAX_AGE_MS) {
      expired++;
      console.warn(
        `Dropping expired offline ${op.type} from ${new Date(op.timestamp).toLocaleDateString()}`,
      );
      continue;
    }

    try {
      await handler(op.payload);
      processed++;
    } catch {
      // Keep for next retry
      failed.push(op);
    }
  }

  saveQueue(failed);
  return { processed, expired };
}

/** Clear the entire queue */
export function clearQueue() {
  localStorage.removeItem(QUEUE_KEY);
}

/** Listen for online/offline events */
export function setupConnectivityListeners(onOnline, onOffline) {
  const handleOnline = () => onOnline?.();
  const handleOffline = () => onOffline?.();

  window.addEventListener("online", handleOnline);
  window.addEventListener("offline", handleOffline);

  return () => {
    window.removeEventListener("online", handleOnline);
    window.removeEventListener("offline", handleOffline);
  };
}
